import GridZone from "./GridZone";
import { Settings } from '../../Settings';
import GridBox from "./GridBox";
import GridTile from "./GridTile";
import Vector from "../math/Vector";
import WorldGrid from "./WorldGrid";
import GridInversionOfControl from "./GridInversionOfControl";
import TileType from "./TileType";
import Fast from "../../util/Fast";
import LightSource from "../lighting/LightSource";
import Character from "../character/Character";

const NEIGHBOR_TOP_INDEX = 0;
const NEIGHBOR_BOTTOM_INDEX = 1;
const NEIGHBOR_RIGHT_INDEX = 2;
const NEIGHBOR_LEFT_INDEX = 3;

export default class Grid extends GridBox {
    /**
     * @param {WorldGrid} worldGrid
     * @param {Vector} origin 
     */
    constructor(worldGrid, origin) {
        super(origin);

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid; 

        /**
         * @type {number}
         */
        this.gridChangeCount = 0;

        /**
         * @constant
         * @private
         * @type {Array.<GridZone>}
         */
        this.zones = this.createZoneArray();

        /**
         * Every grid knows its 4 neighbors.
         * @constant
         * @type {Array.<Grid>}
         */
        this.neighbors = new Array(4);
        this.neighbors[NEIGHBOR_TOP_INDEX] = null;      // Top index
        this.neighbors[NEIGHBOR_BOTTOM_INDEX] = null;   // Bottom index
        this.neighbors[NEIGHBOR_RIGHT_INDEX] = null;    // Right index
        this.neighbors[NEIGHBOR_LEFT_INDEX] = null;     // Left index


        /**
         * @constant
         * @type {Character[]}
         */
        this.characters = [];

        /**
         * @type {Array.<LightSource>}
         */
        this.lightSources = [];

        /**
         * @type {boolean}
         */
        this.loaded = false;
    }

    /**
     * @private
     * @returns {Array.<GridZone>}
     */
    createZoneArray() {
        const zones = new Array(Settings.grid.numZoneColsInGrid * Settings.grid.numZoneRowsInGrid);

        const origin = this.origin;
        const x = origin.x;
        const y = origin.y;

        let i = 0;
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
                zones[i++] = new GridZone(this, new Vector(x + (Settings.grid.zonePixelWidth * col), y + (Settings.grid.zonePixelHeight * row)));
            }
        }

        return zones;
    }

    unloadGrid() {

        if (this.characters.length > 0) {
            const size = this.characters.length;
            for (let i = size - 1; i >= 0; i--) {
                const character = this.characters[i];
                if (character.isMonster()) {
                    if (!character.isFinishedAndAvailable()) {
                        character.reset(true); // <- this will remove the monster from the grid.
                                               // and can affect the list of characters that we are iterating over.
                    }
                }
                else if (character.isPartyMember()) {
                    console.log('Grid.unloadGrid() party character is in grid that is being unloaded.');
                    character.position.resetFull();
                    character.resetTurnState();
                }
            }

            this.characters.length = 0; // Clear the array.
        }

        if (this.lightSources != null) {
            const size = this.lightSources.length;
            for (let i = size - 1; i >= 0; i--) {
                const lightSource = this.lightSources[i];
                if (!lightSource.isFinishedAndAvailable()) {
                    lightSource.reset(true);
                }
            }
            
            this.lightSources.length = 0;
        }

        this.loaded = false
    }


    /**
     * @returns {LightSource[]}
     */
    getLightSources() {
        return this.lightSources;
    }

    /**
     * @param {LightSource} lightSource 
     */
    addLightSource(lightSource) {
        this.lightSources.push(lightSource);
    }
    /**
     * @param {LightSource} lightSource 
     */
    removeLightSource(lightSource) {
        if (this.lightSources == null || this.lightSources.length === 0 || !lightSource) {
            return;
        }
        const index = this.lightSources.indexOf(lightSource);
        if (index >= 0) {
            this.lightSources.splice(index, 1);
        }
    }
    /**
     * @param {GridTile} tile 
     */
    removeLightSourceForTile(tile) {
        if (this.lightSources == null || this.lightSources.length === 0 || tile == null) {
            return;
        }

        for (let i = 0; i < this.lightSources.length; i++) {
            const lightSource = this.lightSources[i];
            if (lightSource.getTile() === tile) {
                this.lightSources.splice[i, 1];
                break;
            }
        }
    }

    /**
     * @param {Character} character 
     */
    addCharacter(character) {
        if (character) {
            this.characters.push(character);

            // Mark the grid as changed.
            ///this.gridStateChanged = true;
        }
    }

    /**
     * @param {Character} character 
     */
    removeCharacter(character) {
        if (character) {
            const index = this.characters.indexOf(character);
            if (index !== -1) {
                this.characters.splice(index, 1);

                // Mark the grid as changed.
                //this.gridStateChanged = true;
            }
        }
    }


    /**
     * This must be invoked whenever a grid is assigned to a new location.
     */
    initializeZonesForGrid() {

        this.gridChangeCount = 0;

        const zones = this.zones;

        const origin = this.origin;
        const x = origin.x;
        const y = origin.y;

        let i = 0;
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
                zones[i++].initialize(x + (Settings.grid.zonePixelWidth * col), y + (Settings.grid.zonePixelHeight * row));
            }
        }
    }

    /**
     * @param {Grid} top 
     * @param {Grid} bottom 
     * @param {Grid} right 
     * @param {Grid} left 
     */
    setGridNeighbors(top, bottom, right, left) {
        this.neighbors[NEIGHBOR_TOP_INDEX] = top;      // Top index
        this.neighbors[NEIGHBOR_BOTTOM_INDEX] = bottom;   // Bottom index
        this.neighbors[NEIGHBOR_RIGHT_INDEX] = right;    // Right index
        this.neighbors[NEIGHBOR_LEFT_INDEX] = left;     // Left index
    }

    /**
     * Invoked when the state of the grid has changed such that it should be saved.
     */
    markGridAsChanged() {
        this.gridChangeCount++;
        this.worldGrid.changeCount++;
    }

    // /**
    //  * @param {number} worldSeed 
    //  */
    // onNewWorldSeed(worldSeed) {
    //     // // If the grid is currently loaded, then we need to unload it
    //     // if (this.zones) {
    //     //     this.unloadGridInternal(false); // <-- false = do not save the grid state.
    //     // }
    // }    

    /**
     * @returns {WorldGrid}
     */
    getWorldGrid() {
        return this.worldGrid;
    }

    /**
     * @returns {boolean}
     */
    isLoaded() {
        return this.loaded;
    }

    /**
     * @returns {Array.<GridZone>}
     */
    getZones() {
        return this.zones;
    }

    /**
     * @returns {number}
     */
    getGridCol() {
        return Fast.floor(this.origin.x / Settings.grid.gridPixelWidth);
    }

    /**
     * @returns {number}
     */
    getGridRow() {
        return Fast.floor(this.origin.y / Settings.grid.gridPixelHeight);
    }

    /**
     * @returns {number}
     */
    static calculateGridCol(tileCol) {
        return Fast.floor(tileCol / Settings.grid.gridTileWidth);
    }

    /**
     * @returns {number}
     */
    static calculateGridRow(tileRow) {
        return Fast.floor(tileRow / Settings.grid.gridTileHeight);
    }

    /**
     * @returns {number}
     */
    getPixelWidth() {
        return Settings.grid.gridPixelWidth;
    }

    /**
     * @returns {number}
     */
    getPixelHeight() {
        return Settings.grid.gridPixelHeight;
    }

    // All the level generation code calls this method.
    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTile(tileCol, tileRow) {
        return this.getGridTileAbsolute(tileCol, tileRow);
    }

    /**
     * @param {Vector} pixelLocation 
     * @returns {GridTile}
     */
    findGridTile(pixelLocation) {
        if (!this.contains(pixelLocation)) {
            return null;
        }

        const tileCol = Fast.floor(pixelLocation.x / Settings.tile.size);
        const tileRow = Fast.floor(pixelLocation.y / Settings.tile.size);

        return this.getGridTileAbsolute(tileCol, tileRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileAbsolute(tileCol, tileRow) {
        const origin = this.origin;
        const tileOriginCol = Fast.floor(origin.x / Settings.tile.size);
        const tileOriginRow = Fast.floor(origin.y / Settings.tile.size);

        return this.getGridTileLocal(tileCol - tileOriginCol, tileRow - tileOriginRow);
    }

    /**
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {GridTile}
     */
    getGridTileLocal(tileCol, tileRow) {

        const zoneCol = Fast.floor(tileCol / Settings.grid.zoneTileWidth);
        const zoneRow = Fast.floor(tileRow / Settings.grid.zoneTileHeight);

        if (zoneCol < 0 || zoneCol >= Settings.grid.numZoneColsInGrid) {
            return null;
        }
        if (zoneRow < 0 || zoneRow >= Settings.grid.numZoneRowsInGrid) {
            return null;
        }

        const index = (zoneCol * Settings.grid.numZoneRowsInGrid) + zoneRow;
        if (index >= 0 && index < this.zones.length) {
            const zone = this.zones[index];

            const gridOrigin = this.origin;
            const gridTileOriginCol = gridOrigin.x / Settings.tile.size;
            const gridTileOriginRow = gridOrigin.y / Settings.tile.size;

            const zoneOrigin = zone.origin;
            const zoneTileOriginCol = zoneOrigin.x / Settings.tile.size;
            const zoneTileOriginRow = zoneOrigin.y / Settings.tile.size;

            return zone.getGridTileLocal(tileCol + gridTileOriginCol - zoneTileOriginCol, tileRow + gridTileOriginRow - zoneTileOriginRow);
        }
        else {
            console.log("Grid.getGridTileLocal() Failed to find tile. col=" + tileCol + " row=" + tileRow + " index=" + index);
            return null;
        }
    }

    /**
     * @param {number} col 
     * @param {number} row 
     * @param {TileType} tileType 
     * @returns {boolean}
     */
    isTileType(col, row, tileType) {
        const tile = this.getGridTile(col, row);
        return tile !== null && tile.getTileType() === tileType; 
    }

    /**
     * @param {number} col 
     * @param {number} row 
     * @param {TileType} tileType 
     * @returns {boolean}
     */
    isNotTileType(col, row, tileType) {
        const tile = this.getGridTile(col, row);
        return tile === null || tile.getTileType() !== tileType;
    }

    /////////////////////////////////
    // Util
    /////////////////////////////////

    /**
     * @returns {Vector}
     */
    createCenterVector() {
        const center = new Vector();
        this.assignCenterVector(center);
        return center;
    }

    /**
     * @param {Vector} center 
     */
    assignCenterVector(center) {
        const halfWidth = Fast.floor(Settings.grid.gridPixelWidth / 2);
        const halfHeight = Fast.floor(Settings.grid.gridPixelHeight / 2);

        const origin = this.getOrigin();
        center.x = origin.x + halfWidth;
        center.y = origin.y + halfHeight;
    }

    ////////////////////////////////////////////////

    /**
     * Finds all the tile neighbors. This should only be called once per grid instance.
     */
    findTileNeighborsFull() {
        for (let i = 0; i < this.zones.length; i++) {
            this.zones[i].findTileNeighborsFull();
        }
    }

    /**
     * @param {WorldGrid} worldGrid
     */
    findWorldGridTileNeighbors() {
        // This is invoked when an overland grid is loaded. We need to make sure that
        // all the grid borders are figured out.

        if (this.neighbors[NEIGHBOR_TOP_INDEX]) {       
            this.findTileNeighborsGridBorderTop();
        }
        else {
            this.nullifyTileNeighborsGridBorderTop();
        }

        if (this.neighbors[NEIGHBOR_BOTTOM_INDEX]) {
            this.findTileNeighborsGridBorderBottom();
        }
        else {
            this.nullifyTileNeighborsGridBorderBottom();
        }

        if (this.neighbors[NEIGHBOR_LEFT_INDEX]) {
            this.findTileNeighborsGridBorderLeft();
        }
        else {
            this.nullifyTileNeighborsGridBorderLeft();
        }

        if (this.neighbors[NEIGHBOR_RIGHT_INDEX]) {
            this.findTileNeighborsGridBorderRight();
        }
        else {
            this.nullifyTileNeighborsGridBorderRight();
        }
    }

    /**
     * Called by the GridLoader during terrain generation.
     */
    evaluateWorldGridTileNeighborBorders() {

        if (this.neighbors[NEIGHBOR_TOP_INDEX]) {       
            this.evaluateTileBordersTop();
        }

        if (this.neighbors[NEIGHBOR_BOTTOM_INDEX]) {
            this.evaluateTileBordersBottom();
        }

        if (this.neighbors[NEIGHBOR_LEFT_INDEX]) {
            this.evaluateTileBordersLeft();
        }

        if (this.neighbors[NEIGHBOR_RIGHT_INDEX]) {
            this.evaluateTileBordersRight();
        }
    }

    /**
     */
    evaluateAllTileBorders() {
        GridInversionOfControl.allTilesInGrid(this, 
            /**
             * @param {GridTile} tile 
             */
            function(tile) {            
                tile.evaluateBorders();
            }
        );
    }
    
    /**
     * Set all border/edge tile neighbors to null.
     * We need to do this prior to terrain generation.
     */
    nullifyBorderNeighbors() {
        this.nullifyTileNeighborsGridBorderRight();
        this.nullifyTileNeighborsGridBorderLeft();
        this.nullifyTileNeighborsGridBorderBottom();
        this.nullifyTileNeighborsGridBorderTop();
    }

    /**
     * @private
     */
    findTileNeighborsGridBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numZoneColsInGrid - 1;
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(eastCol, row).findTileNeighborsZoneBorderRight();
        }
    }
    /**
     * @private
     */
    nullifyTileNeighborsGridBorderRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numZoneColsInGrid - 1;
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(eastCol, row).nullifyTileNeighborsZoneBorderRight();
        }
    }

    /**
     * @private
     */
    evaluateTileBordersRight() {
        // East edge: col = numCols - 1
        const eastCol = Settings.grid.numZoneColsInGrid - 1;
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(eastCol, row).evaluateTileBordersRight();
        }
    }

    /**
     * @private
     */
    findTileNeighborsGridBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(0, row).findTileNeighborsZoneBorderLeft();
        }
    }
    /**
     * @private
     */
    nullifyTileNeighborsGridBorderLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(0, row).nullifyTileNeighborsZoneBorderLeft();
        }
    }

    /**
     * @private
     */
    evaluateTileBordersLeft() {
        // West edge: col = 0
        for (let row = 0; row < Settings.grid.numZoneRowsInGrid; row++) {
            this.findZone(0, row).evaluateTileBordersLeft();
        }
    }

    /**
     * @private
     */
    findTileNeighborsGridBorderBottom() {
        // South is bottom row
        const southRow = Settings.grid.numZoneRowsInGrid - 1;
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, southRow).findTileNeighborsZoneBorderBottom();
        }
    }
    /**
     * @private
     */
    nullifyTileNeighborsGridBorderBottom() {
        // South is bottom row
        const southRow = Settings.grid.numZoneRowsInGrid - 1;
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, southRow).nullifyTileNeighborsZoneBorderBottom();
        }
    }

    /**
     * @private
     */
    evaluateTileBordersBottom() {
        // South is bottom row
        const southRow = Settings.grid.numZoneRowsInGrid - 1;
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, southRow).evaluateTileBordersBottom();
        }
    }

    /**
     * @private
     */
    findTileNeighborsGridBorderTop() {
        // North edge: row = 0
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, 0).findTileNeighborsZoneBorderTop();
        }
    }
    /**
     * @private
     */
    nullifyTileNeighborsGridBorderTop() {
        // North edge: row = 0
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, 0).nullifyTileNeighborsZoneBorderTop();
        }
    }

    /**
     * @private
     */
    evaluateTileBordersTop() {
        for (let col = 0; col < Settings.grid.numZoneColsInGrid; col++) {
            this.findZone(col, 0).evaluateTileBordersTop();
        }
    }

    /**
     * @private
     * @param {number} col Local col index
     * @param {number} row Local row index
     * @returns {GridZone}
     */
    findZone(col, row) {
        return this.zones[(col * Settings.grid.numZoneRowsInGrid) + row];
    }

}